import { Loader2 } from "lucide-react";
import { useTopicGenerationStatusContext } from "@/contexts/TopicGenerationStatusContext";
import type { StreamingPhase } from "@/contexts/TopicGenerationStatusContext";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface GenerationStatusProps {
  isMobile?: boolean;
}

const PHASE_ORDER: StreamingPhase[] = ["topics", "questions", "vocabulary", "speaking-guide"];

const PHASE_LABEL: Record<StreamingPhase, string> = {
  topics: "Generating topic",
  "generating-all": "Generating all content",
  questions: "Creating questions",
  vocabulary: "Building vocabulary",
  "speaking-guide": "Generating speaking guide",
};

export const GenerationStatus = ({ isMobile = false }: GenerationStatusProps) => {
  const { streamingPhase, sessionId, topic } = useTopicGenerationStatusContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!streamingPhase || pathname === "/my-topics") return null;

  const currentIndex = PHASE_ORDER.indexOf(streamingPhase);
  const percent = currentIndex < 0 ? 0 : Math.round(((currentIndex + 1) / PHASE_ORDER.length) * 100);

  return (
    <button
      onClick={() => sessionId && navigate(`/my-topics/edit/${sessionId}`)}
      className={cn(
        "group mb-1.5 w-full text-left rounded-lg border border-primary/20",
        "bg-gradient-to-br from-primary/10 via-accent/30 to-accent/10 backdrop-blur-sm",
        "shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-200 cursor-pointer",
        isMobile && "mb-2"
      )}
      title={topic ? `Open “${topic}”` : PHASE_LABEL[streamingPhase]}
    >
      <div className="px-2.5 py-2 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Loader2 className="flex-shrink-0 animate-spin text-primary w-3 h-3" />
          <span className="flex-1 min-w-0 font-medium text-primary truncate text-[11px]">
            {topic || PHASE_LABEL[streamingPhase]}
          </span>
          <span className="flex-shrink-0 text-[11px] font-semibold text-muted-foreground tabular-nums">
            {percent}%
          </span>
        </div>

        <div className="h-1 w-full bg-secondary/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </button>
  );
};